/**
 * La ficha de un lugar y los cuatro estados de docs/03 §4.3.
 *
 * El copy de los estados es LITERAL y vinculante, con sus prohibiciones:
 * nunca "bloqueado", nunca contador de intentos, nunca cuenta regresiva en
 * segundos, y al publicar se celebra en vez de confirmar una transacción.
 *
 * El estado 2 es la decisión que sostiene el diseño: el formulario ya es
 * editable mientras corre el reloj, con solo el botón en espera. La persona
 * trabaja mientras espera, en vez de esperar para poder trabajar — y de paso
 * mantiene la pantalla al frente, que es lo que el muestreo necesita.
 */

import { useCallback, useEffect, useState } from 'react';

import * as api from '../../shared/lib/api.js';
import { Boton, Pantalla, Stack, Texto, Titulo } from '../../shared/ui/primitives.js';
import { ResumenDeSellos } from '../../shared/ui/Sello.js';
import { clearDraft, readDraft, saveDraft } from './draft.js';
import { useVisitClaim } from './useVisitClaim.js';

/** Mínimo del contrato del servidor. Repetirlo aquí evita un viaje perdido. */
const TEXTO_MIN = 20;

export interface PlaceDetailProps {
  placeId: string;
  onVolver?: (() => void) | undefined;
}

export function PlaceDetail({ placeId, onVolver }: PlaceDetailProps) {
  const [lugar, setLugar] = useState<api.PlaceDetail | null>(null);
  const [carga, setCarga] = useState<'cargando' | 'listo' | 'no_existe' | 'falló'>(
    'cargando',
  );

  const [texto, setTexto] = useState(() => readDraft(placeId));
  const [escribiendo, setEscribiendo] = useState(false);
  const [publicado, setPublicado] = useState(false);
  const [publicando, setPublicando] = useState(false);
  const [selloObtenido, setSelloObtenido] = useState(false);
  const [errorPublicar, setErrorPublicar] = useState<string | null>(null);

  /**
   * TODO(18 de agosto): la ficha no sabe que hay un claim abierto.
   *
   * Al recargar con una visita en curso, la pantalla vuelve al estado 1 y
   * ofrece "Estoy aquí" — que reanuda bien, porque el servidor devuelve el
   * mismo claim con su sello original (ADR-016 §2), así que no se pierde
   * tiempo. Pero la persona ve un botón que ya pulsó y no sabe que su reloj
   * sigue corriendo.
   *
   * Arreglarlo pide que `GET /places/:id` devuelva también el claim abierto,
   * como ya devuelve la llave activa. Se deja fuera de esta tanda porque
   * toca el contrato de una ruta y el camino funciona; queda anotado para
   * que no se descubra otra vez desde cero.
   */
  const visita = useVisitClaim({
    id: placeId,
    lat: lugar?.lat ?? 0,
    lng: lugar?.lng ?? 0,
    kind: lugar?.kind ?? 'standard',
  });

  useEffect(() => {
    let vivo = true;
    void (async () => {
      try {
        const p = await api.fetchPlace(placeId);
        if (!vivo) return;
        if (!p) {
          setCarga('no_existe');
          return;
        }
        setLugar(p);
        setCarga('listo');
      } catch {
        // Distinto de "no existe": aquí la petición falló y la persona merece
        // saberlo, no ver un vacío que parezca real.
        if (vivo) setCarga('falló');
      }
    })();
    return () => {
      vivo = false;
    };
  }, [placeId]);

  const cambiarTexto = useCallback(
    (v: string) => {
      setTexto(v);
      saveDraft(placeId, v);
    },
    [placeId],
  );

  /** La llave que sirve: la que trae la ficha, o la que se acaba de ganar. */
  const tokenId = visita.tokenId ?? lugar?.mine?.activeTokenId ?? null;

  const publicar = useCallback(async () => {
    // Sin token se publica igual (ADR-020). Lo único obligatorio es el texto.
    if (texto.trim().length < TEXTO_MIN) return;
    setPublicando(true);
    setErrorPublicar(null);
    try {
      const r = await api.publishRecommendation(placeId, {
        ...(tokenId ? { tokenId } : {}),
        textoSagrado: texto,
      });
      setSelloObtenido(r.proofLevel === 'sealed');
      // El borrador se borra al CONFIRMAR, nunca al intentar: si la petición
      // falla, lo que la persona escribió sigue ahí.
      clearDraft(placeId);
      setPublicado(true);
    } catch (e) {
      setErrorPublicar(
        e instanceof api.ApiError
          ? (e.body.message as string)
          : 'No pudimos publicar tu opinión. Vuelve a intentar en un momento.',
      );
    } finally {
      setPublicando(false);
    }
  }, [placeId, texto, tokenId]);

  if (carga === 'cargando') {
    return (
      <Pantalla centrada>
        <Texto variante="secundario" centrado>
          Un momento…
        </Texto>
      </Pantalla>
    );
  }

  if (carga === 'no_existe') {
    return (
      <Pantalla centrada>
        <Stack>
          <Titulo centrado>Ese lugar ya no está en el mapa</Titulo>
          {onVolver && <Boton onClick={onVolver}>Volver</Boton>}
        </Stack>
      </Pantalla>
    );
  }

  if (carga === 'falló' || !lugar) {
    return (
      <Pantalla centrada>
        <Stack>
          <Titulo centrado>No pudimos traer este lugar</Titulo>
          <Texto variante="secundario" centrado>
            Vuelve a intentar en un momento.
          </Texto>
          {onVolver && <Boton onClick={onVolver}>Volver</Boton>}
        </Stack>
      </Pantalla>
    );
  }

  // ─── El estado de docs/03 §4.3 que toca ───
  const yaOpino = lugar.mine?.hasRecommended === true;
  const venceEn = lugar.mine?.tokenExpiresAt
    ? new Date(lugar.mine.tokenExpiresAt)
    : visita.expiresAt;
  const llaveExpirada = !tokenId && venceEn !== null && venceEn.getTime() < Date.now();

  const estado: 1 | 2 | 3 | 4 = yaOpino
    ? 3
    : tokenId
      ? 3
      : visita.estado === 'en_el_lugar'
        ? 2
        : llaveExpirada
          ? 4
          : 1;

  /**
   * ADR-020: el token dejó de ser puerta. Lo único que bloquea publicar es no
   * haber escrito nada — y eso no es un candado, es que no hay qué publicar.
   */
  const puedePublicar = texto.trim().length >= TEXTO_MIN && !publicando;

  return (
    <Pantalla>
      <Stack>
        <header>
          <Titulo>{lugar.name}</Titulo>
          {lugar.address && <Texto variante="secundario">{lugar.address}</Texto>}
          <Texto variante="secundario">
            {lugar.recommendationCount === 0
              ? 'Todavía nadie ha opinado'
              : `${lugar.recommendationCount} ${
                  lugar.recommendationCount === 1 ? 'opinión' : 'opiniones'
                }`}
          </Texto>
          {/*
           * El sello, en positivo y sin acusar a nadie (ADR-020). Con cero
           * selladas no dice nada: "0 de 3 estuvieron ahí" sería exactamente
           * la marca negativa que la regla prohíbe.
           */}
          {lugar.recommendationCount > 0 && (
            <Texto variante="secundario">
              <ResumenDeSellos
                total={lugar.recommendationCount}
                selladas={lugar.sealedCount}
              />
            </Texto>
          )}
        </header>

        {publicado && <Celebracion conSello={selloObtenido} />}

        {!publicado && yaOpino && (
          <Banda tono="info">
            <Texto>Ya opinaste de este lugar.</Texto>
          </Banda>
        )}

        {!publicado && !yaOpino && (
          <>
            {estado === 1 && (
              <Banda tono="info">
                <Stack gap="var(--gu-space-3)">
                  {/*
                   * `--gu-info-surface` y NUNCA `--gu-danger`: no es un error,
                   * es un estado normal del producto (docs/03 §4.3).
                   */}
                  {/*
                   * ADR-020: esto YA NO es un muro, es una oferta.
                   *
                   * Antes decía "Aquí solo opina quien estuvo. Cuando llegues,
                   * se desbloquea sola" — con el botón de publicar en gris.
                   * Ahora publicar está disponible siempre; lo que el botón
                   * "Estoy aquí" compra es el SELLO, y se dice qué es.
                   */}
                  <Texto>
                    ¿Estás en el lugar? Tu opinión puede llevar sello — la marca de
                    que estuviste ahí.
                  </Texto>
                  <Boton onClick={() => void visita.empezar()}>Estoy aquí</Boton>
                  {!escribiendo && (
                    <Boton variante="secundario" onClick={() => setEscribiendo(true)}>
                      Escribir sin sello
                    </Boton>
                  )}
                </Stack>
              </Banda>
            )}

            {estado === 2 && (
              <Banda tono="exito">
                {/*
                 * Progreso sin segundos. docs/03 §4.3: una cuenta regresiva
                 * convierte la espera en tarea, y la persona se queda mirando
                 * el número en vez de comiendo.
                 */}
                <Texto>
                  Te reconocimos aquí. Ve disfrutando: tu opinión se desbloquea en unos
                  minutos.
                </Texto>
              </Banda>
            )}

            {estado === 3 && venceEn && (
              <Banda tono={horasPara(venceEn) < 6 ? 'aviso' : 'exito'}>
                <Texto>Llave activa · {restante(venceEn)}</Texto>
              </Banda>
            )}

            {estado === 4 && (
              <Banda tono="aviso">
                {/*
                 * `--gu-warning` y NUNCA `--gu-danger`: no hizo nada malo.
                 * Y no es un callejón — se puede volver a empezar.
                 */}
                <Stack gap="var(--gu-space-3)">
                  <Texto>
                    Se te fue el tiempo, no el lugar. Tu borrador está a salvo: vuelve y
                    lo desbloqueas otra vez.
                  </Texto>
                  <Boton onClick={() => void visita.empezar()}>Estoy aquí</Boton>
                </Stack>
              </Banda>
            )}

            {visita.error && (
              <Banda tono="aviso">
                <Texto>{visita.error}</Texto>
              </Banda>
            )}

            {(escribiendo || estado === 2 || estado === 3 || estado === 4) && (
              <Stack gap="var(--gu-space-3)">
                <label
                  htmlFor="texto-sagrado"
                  style={{
                    fontSize: 'var(--gu-text-caption)',
                    color: 'var(--gu-text-secondary)',
                  }}
                >
                  ¿Por qué es sagrado para ti?
                </label>
                {/*
                 * Editable SIEMPRE, incluso mientras corre el reloj. Es la
                 * decisión que sostiene el diseño del estado 2.
                 */}
                <textarea
                  id="texto-sagrado"
                  aria-label="¿Por qué es sagrado para ti?"
                  value={texto}
                  onChange={(e) => cambiarTexto(e.target.value)}
                  rows={5}
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    padding: 'var(--gu-space-3)',
                    borderRadius: 'var(--gu-radius-md)',
                    border: '1px solid var(--gu-border-subtle)',
                    background: 'var(--gu-surface-raised)',
                    color: 'var(--gu-text-primary)',
                    font: 'inherit',
                    fontSize: 'var(--gu-text-body)',
                    resize: 'vertical',
                  }}
                />

                {errorPublicar && (
                  <Banda tono="aviso">
                    <Texto>{errorPublicar}</Texto>
                  </Banda>
                )}

                <Boton
                  onClick={() => void publicar()}
                  disabled={!puedePublicar}
                >
                  {/*
                   * El botón dice POR QUÉ espera. Un botón gris sin razón es
                   * un callejón; "en un momento" es una promesa con plazo.
                   */}
                  {estado === 2 ? 'Publicar (en un momento)' : 'Publicar'}
                </Boton>
              </Stack>
            )}
          </>
        )}

        {onVolver && (
          <Boton variante="secundario" onClick={onVolver}>
            Volver
          </Boton>
        )}
      </Stack>
    </Pantalla>
  );
}

/**
 * Al publicar se CELEBRA. docs/03 §4.3 prohíbe con nombre y apellido
 * "Enviado correctamente" y "Tu reseña ha sido publicada": son acuses de
 * recibo de un trámite, y lo que acaba de pasar no es un trámite.
 */
function Celebracion({ conSello }: { conSello: boolean }) {
  return (
    <Banda tono="exito">
      <Stack gap="var(--gu-space-2)">
        <Texto>Ya es parte del mapa. Alguien va a comer bien por tu culpa.</Texto>
        {/*
         * El sello se nombra cuando lo hay, y su AUSENCIA nunca se menciona
         * (ADR-020). Publicar sin sello se celebra exactamente igual: quien
         * comió el viernes y escribe el domingo no hizo nada malo.
         */}
        {conSello && <Texto variante="secundario">Con sello: estabas ahí.</Texto>}
      </Stack>
    </Banda>
  );
}

type Tono = 'info' | 'exito' | 'aviso';

/**
 * Las bandas de estado.
 *
 * `--gu-danger` no aparece por ninguna parte a propósito: ninguno de los
 * cuatro estados es un error del usuario, y pintarlos de rojo diría lo
 * contrario sin una sola palabra.
 */
function Banda({ tono, children }: { tono: Tono; children: React.ReactNode }) {
  const fondo: Record<Tono, string> = {
    info: 'var(--gu-info-surface)',
    exito: 'var(--gu-success-surface)',
    aviso: 'var(--gu-warning-surface)',
  };

  return (
    <div
      style={{
        background: fondo[tono],
        borderRadius: 'var(--gu-radius-md)',
        padding: 'var(--gu-space-4)',
      }}
    >
      {children}
    </div>
  );
}

function horasPara(fecha: Date): number {
  return (fecha.getTime() - Date.now()) / 3_600_000;
}

/**
 * Cuánto queda, en la unidad que a alguien le sirve.
 *
 * Días mientras sobra tiempo, horas cuando aprieta. Nunca segundos: eso es la
 * cuenta regresiva que docs/03 §4.3 prohíbe.
 */
function restante(fecha: Date): string {
  const horas = horasPara(fecha);
  if (horas < 1) return 'te queda menos de una hora';
  if (horas < 24) {
    const h = Math.round(horas);
    return `te ${h === 1 ? 'queda' : 'quedan'} ${h} ${h === 1 ? 'hora' : 'horas'}`;
  }
  const dias = Math.round(horas / 24);
  return `te ${dias === 1 ? 'queda' : 'quedan'} ${dias} ${dias === 1 ? 'día' : 'días'}`;
}
